import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { toast } from '../../../design-system/components';
import { track, trackFailed } from '../../../../../shared-web/track';
import {
  minimizeInboundDoor, openInboundDoor, prepareInboundDoor, useInboundDoor, useVisitorConsent, inboundDoorFailureMessage,
} from '../../../../../shared-web/inbound-door';
import { isInAppWebview } from '../../../../../shared-web/ua-detect';
import { GOOGLE_CLIENT_ID, googleSignInHref } from '../../../../../shared-web/google-signin';
import { GoogleG } from '../../../../../shared-web/google-button';
import { BottomSheet } from './BottomSheet';
import { ConsentTerms, CONSENT_AGE } from './ConsentTerms';
import { useHoldsWaitSlot, WaitPillContent } from './InboundWaitOverlay';
import './InboundRegistrationCTA.css';

/** The page to come back to, signed in, when nobody named one. */
function hereNow(): string {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}`;
}

export interface InboundRegistrationCTAProps {
  /** The button's text. */
  label?: string;
  /** Optional glyph drawn before the label, inside the button. */
  icon?: ReactNode;
  /** One line under the sheet's title; defaults to the one-text explanation. */
  description?: string;
  /** Where the browser lands once registered; default is the page it is on. */
  returnTo?: string;
  /** data-log name for the button (registry vocabulary). */
  logName: string;
  /** Which placement of a name this is (data-log-discovery), when one name
   *  renders in several places — the landing's nav pill and section closer. */
  discovery?: string;
  /** Also record the button's exposure (data-log-seen under the same name). */
  seen?: boolean;
  /** The button's classes; default is the primary ember button. A host with
   *  its own button language (the landing hero) passes its own. */
  className?: string;
  /** While a text is awaited, this button becomes the wait's pill in place
   *  (same words, taps back to the code) and the floating pill stands down. */
  holdsWait?: boolean;
  /**
   * The host says, beside this button, what the tap does — one text, Messages
   * opens with it written (the guest invite and the guest door, 2026-11-04). A
   * browser whose consent is on file then goes straight to Messages; from any
   * other button the sheet opens first and says it.
   */
  saysHow?: boolean;
}

/**
 * E09's engine (§4.6.2): the one registration mechanism, as a button.
 *
 * Sending one prefilled text IS the registration — no OTP, no signup form. The
 * flow itself lives in src/shared-web/inbound-door.ts, shared with the
 * marketing bundle's dialog and with /auth, so all three lease, hand off, wait
 * and finish identically. This component is the button plus the sheet that
 * collects the two consents a FIRST visit owes: a browser that has already
 * ticked them goes straight to the composer, because asking twice was never a
 * consent, only a step (2026-08-31).
 *
 * The waiting is not here either. Once the composer has the draft, the app
 * takes over (InboundWaitOverlay, mounted once by EmberApp): the person can
 * leave for Messages, come back to this page — reloaded or not — and still be
 * signed in the moment their text lands. Moving to another page ends the wait
 * (2026-09-03).
 */
export function InboundRegistrationCTA({
  // Neutral default: the same button serves a number that turns out to
  // already have an account — who they are decides the reply, not the label.
  label = 'Continue by text',
  icon,
  description,
  returnTo,
  logName,
  discovery,
  seen = false,
  className = 'ember-btn ember-btn-primary inbound-register-btn',
  holdsWait = false,
  saysHow = false,
}: InboundRegistrationCTAProps): ReactElement {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [over18, setOver18] = useState(false);
  const [consent, setConsent] = useState(false);
  const recorded = useVisitorConsent();
  const { arming, pending, expired } = useInboundDoor();
  useHoldsWaitSlot(holdsWait);

  // ── The Google door(2026-09-09):sheet 里文字门下方的第二扇——授权码重定
  //    向流,自绘按钮整页出发(弃 GIS iframe:弹窗流在第三方 Cookie 被禁的浏览
  //    器里死在空白页)。走查实锤:首页的 Join Ember 从不路过 /auth,那里的
  //    Google 门这批人一辈子见不到。内置浏览器里不摆(Google 封禁 webview OAuth)。 ──
  const googleDoor = Boolean(GOOGLE_CLIENT_ID) && !isInAppWebview();

  /** Is this browser's acceptance still ours to collect? */
  const asksConsent = !(recorded.known && recorded.consented);
  const consentOk = !asksConsent || (over18 && consent);
  const [googleLeaving, setGoogleLeaving] = useState(false);

  const goGoogle = async (): Promise<void> => {
    if (googleLeaving) return;
    if (!consentOk) { toast.error('First, the two checkboxes above.'); return; }
    setGoogleLeaving(true);
    track('login.google_requested', { arrival: 'register_cta' });
    // 同意记录只在勾选框真的画在屏幕上时才补写;账号级的接受由服务端在建号时落
    if (asksConsent) {
      await fetch('/api/visitor/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ age_confirmed: true, tos_accepted: true, door: 'register' }),
      }).catch(() => {});
    }
    window.location.assign(googleSignInHref(returnTo || hereNow()));
  };

  const openMessages = async (tosAcceptedAt: string): Promise<void> => {
    if (arming) return;
    track('login.handoff_requested', { arrival: 'register_cta' });
    const result = await openInboundDoor({
      arrival: 'register_cta',
      // The SPA's doors complete the plain onboarding (no story registration,
      // no welcome text) — see OpenDoorInput.surface and
      // docs/login-and-registration-doors.md.
      surface: 'me',
      returnTo: returnTo || hereNow(),
      tosAcceptedAt,
    });
    if (!result.ok) {
      track('login.failed', { arrival: 'register_cta', step: 'handoff', reason: result.code });
      // The consent record stopped answering (a Terms bump between the read and
      // this tap): dock the sheet so there is something to satisfy the refusal
      // with, instead of a toast the person cannot act on.
      if (result.code === 'tos_acceptance_required' || result.code === 'age_confirmation_required') {
        setSheetOpen(true);
      }
      toast.error(inboundDoorFailureMessage(result.code));
      return;
    }
    // The overlay owns everything from here — the code, the wait, the phone
    // panel, the sign-in — so the sheet has nothing left to say.
    setSheetOpen(false);
  };

  // 一扇门时直走,两扇门时先问走哪扇(2026-09-09 拍板,推翻同意在档就直冲短信
  // 的快捷路径——勾过同意的老浏览器在这些入口一辈子见不到 Google)。sheet 里
  // 勾选框只在这个浏览器还没答过时才画。直走只给旁边已经说清楚「一条短信、点了打开
  // Messages」的按钮(saysHow,2026-11-04 founder:对没有心理准备的用户太突兀);
  // 别的按钮先开 sheet,由 sheet 说。
  const goesStraight = saysHow && recorded.known && recorded.consented && !googleDoor;
  const start = (): void => {
    if (goesStraight) {
      void openMessages('');
      return;
    }
    setSheetOpen(true);
  };

  // The code is made ready while the person is one tap from sending it
  // (inbound-door.ts prepareInboundDoor, 2026-11-04), so that tap opens
  // Messages itself: a button that goes straight there, the sheet open on a
  // browser whose consent is on file, or both boxes ticked — the acceptance
  // goes with that lease, stamped at the tick.
  // Until it is in hand the sheet's button waits, so its tap is always the one
  // that opens Messages (prod: the lease takes 170 ms at the median, and one
  // person in ten taps within half a second of the last tick).
  const oneTapAway = goesStraight || (sheetOpen && recorded.known && consentOk);
  const [readying, setReadying] = useState(false);
  useEffect(() => {
    if (!oneTapAway || pending) return undefined;
    let live = true;
    setReadying(true);
    void prepareInboundDoor({
      arrival: 'register_cta',
      surface: 'me',
      returnTo: returnTo || hereNow(),
      tosAcceptedAt: asksConsent ? new Date().toISOString() : '',
    }).then(() => { if (live) setReadying(false); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneTapAway, pending]);

  if (holdsWait && pending) {
    return (
      <button
        type="button"
        className={`${className} is-waiting`}
        data-log="login_wait_show"
        data-log-discovery={discovery}
        onClick={() => minimizeInboundDoor(false)}
      >
        <WaitPillContent expired={expired} />
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className={className}
        data-log={logName}
        data-log-seen={seen ? logName : undefined}
        data-log-discovery={discovery}
        disabled={arming}
        onClick={start}
      >
        {icon}
        <span>{arming ? 'Preparing…' : label}</span>
      </button>

      {sheetOpen && (
        <BottomSheet
          onClose={() => setSheetOpen(false)}
          // One registration module, one set of sentences (2026-09-05): the
          // sheet says what /auth says — the page and the sheet are the same
          // door met in two places.
          title="Continue with a text"
          description={description || 'Your phone number is your account — new or returning.'}
          logSeen="register_inbound_seen"
          // discovery = which CTA docked the sheet (talk_quota_register /
          // result_register_open …). Without a distinguishing prop the seen
          // deduped once per document across ALL hosts, so only the first
          // surface a visitor met ever recorded its sheet.
          // state = what the sheet asked of them (2026-11-02): the two boxes or
          // not (this browser's consent on file), and whether Google sat beside
          // the text door — a sheet left without a tap reads against it.
          logSeenAttrs={{
            'data-log-discovery': logName,
            'data-log-state': `${asksConsent ? 'boxes' : 'no_boxes'}${googleDoor ? '_google' : ''}`,
          }}
          className="inbound-register-sheet"
        >
          {/* What joining keeps, said as what they will notice (2026-11-04 founder). */}
          <ul className="inbound-register-keeps">
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6.5 3.5h11v17l-5.5-3.6-5.5 3.6z" /></svg>
              <span>Come back anytime &mdash; no starting over</span>
            </li>
            <li>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 4h3.5l1.7 4.3-2.2 1.4a11 11 0 0 0 6.3 6.3l1.4-2.2L20 15.5V19a1.5 1.5 0 0 1-1.6 1.5C10.6 20 4 13.4 3.5 5.6A1.5 1.5 0 0 1 5 4z" /></svg>
              <span>Text or call Ember from your phone, day or night</span>
            </li>
          </ul>
          {/* Same two statements as the 2026-08-20 Messages door: the
              registration text is also the texting consent. Asked once per
              browser — the record answers for every later door, so a browser
              that already ticked them sees only the two doors. */}
          {asksConsent && (
          <div className="inbound-register-consents">
            <label className="inbound-register-check">
              <input
                type="checkbox"
                checked={over18}
                disabled={arming}
                data-log="login_age_confirm"
                data-log-state={over18 ? 'off' : 'on'}
                onChange={(e) => setOver18(e.target.checked)}
              />
              <span>{CONSENT_AGE}</span>
            </label>
            <label className="inbound-register-check">
              <input
                type="checkbox"
                checked={consent}
                disabled={arming}
                data-log="login_tos_agree"
                data-log-state={consent ? 'off' : 'on'}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <ConsentTerms door="register" rel="noopener noreferrer" termsLog="register_terms_open" privacyLog="register_privacy_open" />
            </label>
          </div>
          )}
          {/* Before the two boxes are ticked the button looks not-yet but still
              takes the tap (2026-09-23): a disabled button swallows the press
              with no row and no word, and the sheet's most likely dead end —
              press the big button first, nothing happens, leave — was
              invisible. Blocked, the tap is the failure twin
              ui.failed register_inbound_open / consent_missing, and the name
              register_inbound_open itself stays the tap that opens the composer. */}
          <button
            type="button"
            className="ember-btn ember-btn-primary inbound-register-open"
            data-log={consentOk ? 'register_inbound_open' : undefined}
            disabled={arming || (consentOk && readying)}
            aria-disabled={consentOk ? undefined : true}
            onClick={() => {
              if (!consentOk) {
                trackFailed('register_inbound_open', 'consent_missing');
                toast.error('First, the two checkboxes above.');
                return;
              }
              void openMessages(asksConsent ? new Date().toISOString() : '');
            }}
          >
            {arming || (consentOk && readying) ? 'Preparing…' : 'Continue by text'}
          </button>
          {/* What the tap does, said before it (2026-11-04 founder; until then
              the wait overlay said it after, 2026-09-06): the tap itself opens
              Messages now, so the person meets the composer before any page of
              ours could explain it. */}
          <p className="inbound-register-how">Messages opens with one short text, already written. Tap send and you&rsquo;re in.</p>
          {googleDoor && (
            <div className="inbound-register-alt">
              <div className="inbound-register-or" aria-hidden><span />or<span /></div>
              <button
                type="button"
                className="ember-btn ember-btn-secondary inbound-register-google-btn"
                data-log="register_google"
                disabled={googleLeaving}
                onClick={() => { void goGoogle(); }}
              >
                <GoogleG size={18} />
                <span>{googleLeaving ? 'Opening Google…' : 'Continue with Google'}</span>
              </button>
            </div>
          )}
        </BottomSheet>
      )}
    </>
  );
}
